// One-click copy for the affiliate portal.
//
// Affiliates paste promo assets into mail clients and page builders, so an
// email swipe has to arrive as formatted HTML, not as a wall of tags. Every
// path below therefore tries to put both flavours on the clipboard
// (`text/html` and `text/plain`) and only gives up the rich one when the
// browser leaves no way to carry it.
//
// `fallback` is the component's `this.$copyText` (v-clipboard); it returns a
// promise too.

// `document.execCommand('copy')` fires a `copy` event first, and a handler can
// replace what actually lands on the clipboard — including the HTML flavour.
// This is the only rich-copy route on a non-secure origin, where
// `navigator.clipboard` does not exist at all.
function copyWithCopyEvent({ text, html }) {
  if (typeof document === 'undefined' || typeof document.execCommand !== 'function') {
    return false;
  }

  const handler = event => {
    const clipboardData = event.clipboardData || (typeof window !== 'undefined' && window.clipboardData);

    if (!clipboardData) {
      return;
    }

    if (html) {
      clipboardData.setData('text/html', html);
    }

    clipboardData.setData('text/plain', text);
    event.preventDefault();
  };

  // execCommand only copies when there is a selection, so park the plain text
  // in an off-screen node and select that; the handler above decides what is
  // really written. The caller's selection is restored either way.
  const carrier = document.createElement('div');
  carrier.textContent = text;
  carrier.setAttribute('aria-hidden', 'true');
  carrier.style.cssText = 'position:fixed;top:0;left:-9999px;white-space:pre;';
  document.body.appendChild(carrier);

  const selection = typeof window !== 'undefined' && window.getSelection ? window.getSelection() : null;
  const previousRange = selection && selection.rangeCount ? selection.getRangeAt(0) : null;
  let copied = false;

  // A plain capture-phase registration, removed below with the same boolean:
  // `{ once: true }` would leave the listener in place whenever execCommand
  // never fires a copy event, and options-object support is not universal.
  document.addEventListener('copy', handler, true);

  try {
    if (selection && typeof document.createRange === 'function') {
      const range = document.createRange();
      range.selectNodeContents(carrier);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    copied = document.execCommand('copy');
  } catch (error) {
    copied = false;
  } finally {
    document.removeEventListener('copy', handler, true);

    if (selection) {
      selection.removeAllRanges();

      if (previousRange) {
        selection.addRange(previousRange);
      }
    }

    if (carrier.parentNode) {
      carrier.parentNode.removeChild(carrier);
    }
  }

  return copied;
}

export function copyAsset({ text = '', html = '' }, fallback) {
  const value = text || '';
  const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : null;

  // Selecting, copying and restoring the selection is disruptive enough that
  // one attempt per call is the limit, however many routes fall through to it.
  let copyEventTried = false;
  const tryCopyEvent = () => {
    if (copyEventTried) {
      return false;
    }

    copyEventTried = true;
    return copyWithCopyEvent({ text: value, html });
  };

  const legacyCopy = () => {
    if (tryCopyEvent()) {
      return Promise.resolve();
    }
    if (typeof fallback !== 'function') {
      return Promise.reject(new Error('No clipboard available'));
    }
    try {
      return Promise.resolve(fallback(value));
    } catch (error) {
      return Promise.reject(error);
    }
  };
  const copyPlainText = () => {
    if (!clipboard || typeof clipboard.writeText !== 'function') {
      return legacyCopy();
    }
    try {
      return Promise.resolve(clipboard.writeText(value)).catch(legacyCopy);
    } catch (error) {
      return legacyCopy();
    }
  };

  // Cross-origin embeds often have no clipboard-write permission. Copy while
  // the click's user activation is still live, before an async denial can
  // consume it. This also preserves rich HTML in existing vendor embeds.
  const embedded = typeof window !== 'undefined' && window.self !== window.top;
  if (embedded && tryCopyEvent()) {
    return Promise.resolve();
  }

  const canWriteRichText =
    Boolean(html)
    && Boolean(clipboard)
    && typeof clipboard.write === 'function'
    && typeof window !== 'undefined'
    && typeof window.ClipboardItem === 'function';

  if (canWriteRichText) {
    try {
      const item = new window.ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([value], { type: 'text/plain' }),
      });
      return Promise.resolve(clipboard.write([item])).catch(() => {
        if (tryCopyEvent()) {
          return undefined;
        }
        return copyPlainText();
      });
    } catch (error) {
      if (tryCopyEvent()) {
        return Promise.resolve();
      }
      return copyPlainText();
    }
  }

  // Rich assets retain both flavours even without ClipboardItem support.
  if (html && tryCopyEvent()) {
    return Promise.resolve();
  }
  return copyPlainText();
}

export default copyAsset;
